import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Icon from "../../../../Icon/Icon";
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import { EGSpeedDial } from "../../../../MainComponents/Button/SpeedDial/SpeedDial";
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { useConstants } from "../Main/useConstants";
import _ from "lodash";

export const ResourceTypes = ({
  resources,
  handleSaveResources,
  setSelectedResource,
  setIsEditing,
  isLoading,
  justView,
  openConfirmDeleteModal,
  closeModalConfirm,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const handleApproveResource = async (key, idx, value) => {
    const newResources = _.cloneDeep(resources);
    newResources[key][idx].approved = value;
    await handleSaveResources(newResources);
  };

  const openModalReopenInvestigation = ({ key, idx }) => {
    openConfirmDeleteModal({
      onConfirm: () => handleRemoveResource({ key, idx }),
    });
  };

  const handleRemoveResource = async ({ key, idx }) => {
    const newResources = _.cloneDeep(resources);
    const resourcesOfAKind = _.get(newResources, `${key}`);
    resourcesOfAKind.splice(idx, 1);
    await handleSaveResources(newResources);
    closeModalConfirm();
  };

  const handleClickEditResource = ({ key, idx }) => {
    setSelectedResource({ key, idx });
    setIsEditing(true);
  };

  const actions = [
    {
      color: "success",
      name: t("resources.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.primary.main} />,
      onClick: (_, props) => handleClickEditResource(props),
    },
    {
      color: "error",
      name: t("resources.delete"),
      icon: <Icon name="trash_can" color={theme.palette.error.main} />,
      onClick: (_, props) => openModalReopenInvestigation(props),
    },
  ];

  return (
    <Stack spacing={2} padding={justView ? 0 : 2}>
      <AccessResources
        resources={resources?.access}
        handleApproveResource={handleApproveResource}
        actions={actions}
        isLoading={isLoading}
        justView={justView}
      />
      <FinancialResources
        resources={resources?.financial}
        handleApproveResource={handleApproveResource}
        actions={actions}
        isLoading={isLoading}
        justView={justView}
      />
      <HumanResources
        resources={resources?.human}
        handleApproveResource={handleApproveResource}
        actions={actions}
        isLoading={isLoading}
        justView={justView}
      />
      <MaterialResources
        resources={resources?.material}
        handleApproveResource={handleApproveResource}
        actions={actions}
        isLoading={isLoading}
        justView={justView}
      />
    </Stack>
  );
};

const AccessResources = ({
  resources,
  actions,
  justView,
  handleApproveResource,
  isLoading,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const {
    resources: { accessResourcesOpts },
  } = useConstants();

  if (!Array.isArray(resources) || resources.length === 0) return <></>;

  return (
    <EGAccordionSingle 
      title={t("resources.access_resource")} 
      color={theme.palette.white.main}
      bgColor={theme.palette.aquaSky.main}
    >
      <Stack spacing={2} paddingX={0.8} paddingY={0.8}>
        {resources.map((item, idx) => (
          <InvestigationItemContainer
            key={idx}
            color={theme.palette.aquaSky.main}
            sx={{ padding: 2 }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography>
                {t("resources.resource")}:&nbsp;
                <Typography
                  fontWeight={600}
                  color="aquaSky"
                  display="inline"
                  component="span"
                >
                  {accessResourcesOpts.find(
                    (typeRes) => typeRes.value === item.resource
                  )?.label ?? t("resources.no_specified")}
                </Typography>
              </Typography>

              {!justView && (
                <Box style={{ minWidth: "35px", position: "relative" }}>
                  <EGSpeedDial
                    direction="left"
                    actions={actions}
                    props={{ key: "access", idx }}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 0,
                      transform: "translateY(calc(-50% - 5px))",
                      scale: 0.8,
                    }}
                  />
                </Box>
              )}
            </Stack>

            <hr />

            <Box
              display="flex"
              justifyContent="space-between"
              maxWidth={400}
              mb={1.5}
            >
              <Typography
                fontSize={16}
                fontWeight={600}
                color={theme.palette.secondary.main}
              >
                {item.approved
                  ? t("resources.resource_approved")
                  : t("resources.approve_resource")}
              </Typography>
              <EGSwitch
                checked={!!item.approved}
                disabled={isLoading || justView}
                onChange={() =>
                  handleApproveResource("access", idx, !item.approved)
                }
                size="small"
              />
            </Box>

            <Grid2 container spacing={2}>
              {item.details && (
                <InvestigationBlockInfo
                  label={t("resources.details")}
                  text={item.details}
                  size={12}
                />
              )}
            </Grid2>
          </InvestigationItemContainer>
        ))}
      </Stack>
    </EGAccordionSingle>
  );
};

const FinancialResources = ({
  resources,
  actions,
  justView,
  handleApproveResource,
  isLoading,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  if (!Array.isArray(resources) || resources.length === 0) return <></>;

  const totalApproved = resources.reduce((acc, obj) => {
    const amount = Number(obj.amount);
    return obj.approved && !isNaN(amount) ? acc + amount : acc;
  }, 0);

  return (
    <EGAccordionSingle 
      title={t("resources.financial_resource")}
      color={theme.palette.white.main}
      bgColor={theme.palette.teal.main}
    >
      <Stack spacing={2} paddingX={0.8} paddingY={0.8}>
        {resources.map((resource, idx) => (
          <InvestigationItemContainer
            key={idx}
            color={theme.palette.teal.main}
            sx={{ padding: 2 }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography>
                {t("resources.resource")}: &nbsp;
                <Typography
                  component="span"
                  fontWeight={600}
                  color={theme.palette.teal.main}
                  display="inline"
                >
                  {resource.resource}
                </Typography>
              </Typography>

              {!justView && (
                <Box style={{ minWidth: "35px", position: "relative" }}>
                  <EGSpeedDial
                    direction="left"
                    actions={actions}
                    props={{ key: "financial", idx }}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 0,
                      transform: "translateY(calc(-50% - 5px))",
                      scale: 0.8,
                    }}
                  />
                </Box>
              )}
            </Stack>

            <hr />

            <Stack direction="row" justifyContent="space-between">
              <Box
                display="flex"
                justifyContent="space-between"
                width={400}
                maxWidth={400}
                mb={1.5}
              >
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  color={theme.palette.secondary.main}
                >
                  {resource.approved
                    ? t("resources.resource_approved")
                    : t("resources.approve_resource")}
                </Typography>
                <EGSwitch
                  checked={!!resource.approved}
                  disabled={isLoading || justView}
                  onChange={() =>
                    handleApproveResource("financial", idx, !resource.approved)
                  }
                  size="small"
                />
              </Box>

              <Box>
                <Typography fontSize={16} fontWeight={600}>
                  ${resource.amount}
                </Typography>
              </Box>
            </Stack>

            <Grid2 container spacing={2}>
              {resource.details && (
                <InvestigationBlockInfo
                  label={t("resources.details")}
                  text={resource.details}
                  size={12}
                />
              )}
            </Grid2>
          </InvestigationItemContainer>
        ))}
        {totalApproved > 0 && (
          <Box display="flex" justifyContent="end">
            <Box
              border={`2px solid ${theme.palette.ethicsBlue.main}`}
              display="inline-block"
              padding="5px 10px"
              borderRadius={1}
            >
              <Typography fontSize={12}>
                {t("resources.total_aproved")}:&nbsp;
                <Typography
                  component="span"
                  display="inline"
                  fontWeight={600}
                  color={theme.palette.ethicsBlue.main}
                >
                  ${totalApproved}
                </Typography>
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </EGAccordionSingle>
  );
};

const HumanResources = ({
  resources,
  actions,
  justView,
  handleApproveResource,
  isLoading,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const {
    resources: { collaboratorOpts, expertiseOpts, roles },
  } = useConstants();

  if (!Array.isArray(resources) || resources.length === 0) return <></>;

  return (
    <EGAccordionSingle 
      title={t("resources.human_resource")}
      color={theme.palette.white.main}
      bgColor={"#7BBDFF"}
    >
      <Stack spacing={2} paddingX={0.8} paddingY={0.8}>
        {resources.map((resource, idx) => (
          <InvestigationItemContainer
            key={idx}
            color="#7BBDFF"
            sx={{ padding: 2 }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography>
                {t("resources.name")}:&nbsp;
                <Typography
                  fontWeight={600}
                  color="#007B6F"
                  display="inline"
                  component="span"
                >
                  {resource.name}
                </Typography>
              </Typography>

              {!justView && (
                <Box style={{ minWidth: "35px", position: "relative" }}>
                  <EGSpeedDial
                    direction="left"
                    actions={actions}
                    props={{ key: "human", idx }}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 0,
                      transform: "translateY(calc(-50% - 5px))",
                      scale: 0.8,
                    }}
                  />
                </Box>
              )}
            </Stack>

            <hr />

            <Grid2 container spacing={2}>
              <Grid2
                size={{ xs: 12, lg: 4 }}
                display="flex"
                justifyContent="space-between"
              >
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  color={theme.palette.secondary.main}
                >
                  {resource.approved
                    ? t("resources.resource_approved")
                    : t("resources.approve_resource")}
                </Typography>
                <EGSwitch
                  checked={!!resource.approved}
                  disabled={isLoading || justView}
                  onChange={() =>
                    handleApproveResource("human", idx, !resource.approved)
                  }
                  size="small"
                />
              </Grid2>
              <InvestigationBlockInfo
                label={t("resources.email")}
                text={
                  resource.email ? resource.email : t("resources.no_specified")
                }
                size={{ xs: 12, lg: 4 }}
                icon="outline_sms"
              />
              <InvestigationBlockInfo
                label={t("resources.collaborator_type")}
                text={
                  collaboratorOpts.find(
                    (item) => item.value === resource.collaborator
                  )?.label ?? t("resources.no_specified")
                }
                size={{ xs: 12, lg: 4 }}
              />
              <InvestigationBlockInfo
                label={t("resources.expertise")}
                text={
                  expertiseOpts.find(
                    (item) => item.value === resource.expertise
                  )?.label ?? t("resources.no_specified")
                }
                size={{ xs: 12, lg: 6 }}
              />
              <InvestigationBlockInfo
                label={t("resources.phone")}
                text={
                  resource.phone ? resource.phone : t("resources.no_specified")
                }
                size={{ xs: 12, lg: 6 }}
                icon={!resource.phone ? "bold_info_remove" : undefined}
                iconColor={theme.palette.red.main}
              />
              {Array.isArray(resource.roles) && resource.roles.length > 0 && (
                <Grid2 size={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    color={theme.palette.secondary.main}
                  >
                    {t("resources.assigned_roles")}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={1.5}
                  >
                    {resource.roles.map((rol) => (
                      <Stack direction="row" alignItems="center" key={rol}>
                        <Icon
                          name="bold_tag_right"
                          style={{ marginRight: 10 }}
                          color={theme.palette.blueGray100.main}
                          size={14}
                        />
                        <Typography
                          color="secondary"
                          fontWeight={600}
                          fontSize={16}
                        >
                          {roles[rol]}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid2>
              )}
              {resource.other_activities && (
                <InvestigationBlockInfo
                  label={t("resources.other_research_activities")}
                  text={resource.other_activities}
                  size={12}
                  underline={false}
                />
              )}
              {resource.details && (
                <InvestigationBlockInfo
                  label={t("resources.investigation_details")}
                  labelColor={theme.palette.ethicsBlue.main}
                  text={resource.details}
                  size={12}
                  underline={false}
                />
              )}
            </Grid2>
          </InvestigationItemContainer>
        ))}
      </Stack>
    </EGAccordionSingle>
  );
};

const MaterialResources = ({
  resources,
  actions,
  justView,
  handleApproveResource,
  isLoading,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  if (!Array.isArray(resources) || resources.length === 0) return <></>;

  return (
    <EGAccordionSingle 
      title={t("resources.material_resource")}
      color={theme.palette.white.main}
      bgColor={theme.palette.vividViolet.main}
    >
      <Stack spacing={2} paddingX={0.8} paddingY={0.8}>
        {resources.map((item, idx) => (
          <InvestigationItemContainer
            key={idx}
            color={theme.palette.vividViolet.main}
            sx={{ padding: 2 }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography>
                {t("resources.resource")}:&nbsp;
                <Typography
                  component="span"
                  fontWeight={600}
                  color={theme.palette.vividViolet.main}
                  display="inline"
                >
                  {item.resource}
                </Typography>
              </Typography>

              {!justView && (
                <Box style={{ minWidth: "35px", position: "relative" }}>
                  <EGSpeedDial
                    direction="left"
                    actions={actions}
                    props={{ key: "material", idx }}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 0,
                      transform: "translateY(calc(-50% - 5px))",
                      scale: 0.8,
                    }}
                  />
                </Box>
              )}
            </Stack>

            <hr />

            <Box
              display="flex"
              justifyContent="space-between"
              maxWidth={400}
              mb={1.5}
            >
              <Typography
                fontSize={16}
                fontWeight={600}
                color={theme.palette.secondary.main}
              >
                {item.approved
                  ? t("resources.resource_approved")
                  : t("resources.approve_resource")}
              </Typography>
              <EGSwitch
                checked={!!item.approved}
                disabled={isLoading || justView}
                onChange={() =>
                  handleApproveResource("material", idx, !item.approved)
                }
                size="small"
              />
            </Box>

            <Grid2 container spacing={2}>
              {item.details && (
                <InvestigationBlockInfo
                  label={t("resources.details")}
                  text={item.details}
                  size={12}
                />
              )}
            </Grid2>
          </InvestigationItemContainer>
        ))}
      </Stack>
    </EGAccordionSingle>
  );
};
